import type {
  StrategyRdDirection,
  StrategyRdMatch,
  StrategyRdMatchMarker,
  StrategyRdOhlcBar,
  StrategyRdOhlcStats,
} from "../types/strategyRd";
import { strategyRdMatchKey } from "./strategyRd";
import { strategyRdConceptChartLabel } from "./strategyRdConceptCopy";
import { executionCandleTimeToMillis } from "./vela/executionCandlesToVelaBars";
import {
  strategyRdMatchDisplayDirection,
  strategyRdPathChartMarkers,
  strategyRdPathEventChartLabel,
  strategyRdPathIncompleteChartLabel,
} from "./strategyRdPath";

export type StrategyRdDetectMode = "preview" | "scan";

export type StrategyRdDetectHighlight = {
  barTime: string;
  direction: StrategyRdDirection;
  label: string;
  showText: boolean;
  showArrow?: boolean;
  /** When false, no column wash — selected path uses labels only. */
  showWash?: boolean;
  emphasis?: "normal" | "strong" | "muted";
  kind?:
    | "setup"
    | "break"
    | "rejection"
    | "retest"
    | "location_armed"
    | "respect"
    | "reject"
    | "break_retest"
    | "entry"
    | "invalid"
    | "marker";
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : value == null ? "" : String(value);
}

function finiteNum(value: unknown): number | null {
  // Number(null) === 0 — never coerce null/undefined/"" into a fake price.
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function parseDirection(raw: unknown): StrategyRdDirection {
  const d = asString(raw).trim().toLowerCase();
  if (d === "short") return "short";
  if (d === "neutral") return "neutral";
  return "long";
}

export function parseStrategyRdMatchMarker(raw: unknown): StrategyRdMatchMarker | null {
  const row = asRecord(raw);
  if (!row) return null;
  const barTime = asString(row.bar_time || row.timestamp || row.time).trim();
  if (!barTime) return null;
  const patternId =
    asString(row.pattern_id).trim() || asString(row.setup_id).trim() || null;
  const barIndex = finiteNum(row.bar_index);
  const volumeStatusRec = asRecord(row.volume_status);
  const volumeAvailable =
    typeof row.volume_available === "boolean"
      ? row.volume_available
      : typeof volumeStatusRec?.available === "boolean"
        ? volumeStatusRec.available
        : null;
  const volExpanded =
    typeof row.vol_expanded === "boolean"
      ? row.vol_expanded
      : row.vol_expanded === "true"
        ? true
        : row.vol_expanded === "false"
          ? false
          : null;
  const volume = finiteNum(row.volume);
  const volumePrior = finiteNum(row.volume_prior);
  const volExpandRatio = finiteNum(row.vol_expand_ratio);
  const volExpandFactor = finiteNum(row.vol_expand_factor);
  const rvol = finiteNum(row.rvol);
  const rvolAvg = finiteNum(row.rvol_avg);
  const rvolLookback = finiteNum(row.rvol_lookback);
  return {
    bar_time: barTime,
    bar_index: barIndex,
    direction: parseDirection(row.direction),
    pattern_id: patternId,
    setup_id: asString(row.setup_id).trim() || patternId,
    session_id: asString(row.session_id).trim() || null,
    ...(volume != null ? { volume } : {}),
    ...(volumePrior != null ? { volume_prior: volumePrior } : {}),
    ...(volExpandRatio != null ? { vol_expand_ratio: volExpandRatio } : {}),
    ...(volExpandFactor != null ? { vol_expand_factor: volExpandFactor } : {}),
    ...(volExpanded != null ? { vol_expanded: volExpanded } : {}),
    ...(rvol != null ? { rvol } : {}),
    ...(rvolAvg != null ? { rvol_avg: rvolAvg } : {}),
    ...(rvolLookback != null ? { rvol_lookback: rvolLookback } : {}),
    ...(volumeAvailable != null ? { volume_available: volumeAvailable } : {}),
    ...(volumeStatusRec
      ? {
          volume_status: {
            available:
              typeof volumeStatusRec.available === "boolean"
                ? volumeStatusRec.available
                : volumeAvailable,
            reason:
              typeof volumeStatusRec.reason === "string"
                ? volumeStatusRec.reason
                : null,
          },
        }
      : {}),
  };
}

export function parseStrategyRdMatchMarkers(
  raw: unknown
): StrategyRdMatchMarker[] {
  if (!Array.isArray(raw)) return [];
  const out: StrategyRdMatchMarker[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const marker = parseStrategyRdMatchMarker(item);
    if (!marker) continue;
    const key = `${marker.bar_time}|${marker.pattern_id || ""}|${marker.direction}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(marker);
  }
  return out;
}

export function strategyRdMatchMarkersFromMatches(
  matches: readonly StrategyRdMatch[] | null | undefined
): StrategyRdMatchMarker[] {
  const out: StrategyRdMatchMarker[] = [];
  const seen = new Set<string>();
  for (const match of matches ?? []) {
    const barTime = match.bar_time?.trim();
    if (!barTime) continue;
    const patternId = (match.setup_id || match.pattern_id || "").trim() || null;
    const direction = strategyRdMatchDisplayDirection(match);
    const key = `${barTime}|${patternId || ""}|${direction}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      bar_time: barTime,
      bar_index: match.path?.events.find((e) => e.kind === "setup")?.bar_index ?? null,
      direction,
      pattern_id: patternId,
      setup_id: match.setup_id ?? patternId,
      session_id:
        typeof match.meta?.session_id === "string" ? match.meta.session_id : null,
    });
  }
  return out;
}

/** Prefer BE `match_markers`; fall back to mapping `matches[]`. */
export function resolveStrategyRdMatchMarkers(
  markers: readonly StrategyRdMatchMarker[] | null | undefined,
  matches: readonly StrategyRdMatch[] | null | undefined
): StrategyRdMatchMarker[] {
  if (markers && markers.length > 0) return [...markers];
  return strategyRdMatchMarkersFromMatches(matches);
}

/** Primary series is top-level candles; keep the previous window if the new payload omits them. */
export function strategyRdDetectWindowCandles(
  incoming: readonly StrategyRdOhlcBar[] | null | undefined,
  previous: readonly StrategyRdOhlcBar[] | null | undefined
): StrategyRdOhlcBar[] {
  if (incoming && incoming.length > 0) return [...incoming];
  return previous && previous.length > 0 ? [...previous] : [];
}

/** True when at least one bar carries a finite volume (histogram can paint). */
export function strategyRdCandlesHaveVolume(
  candles: readonly StrategyRdOhlcBar[] | null | undefined
): boolean {
  if (!candles?.length) return false;
  for (const bar of candles) {
    const v = bar.volume;
    if (v != null && Number.isFinite(Number(v))) return true;
  }
  return false;
}

/** True when a bar has real participation (zeros do not open the volume pane). */
export function strategyRdCandlesHavePositiveVolume(
  candles: readonly { volume?: number | null }[] | null | undefined
): boolean {
  if (!candles?.length) return false;
  for (const bar of candles) {
    const v = bar.volume;
    if (v != null && Number.isFinite(Number(v)) && Number(v) > 0) return true;
  }
  return false;
}

/**
 * Warm preview candles to echo back to BE — omit when they lack volume so BE can
 * serve the volume-bearing OHLC window (histogram + expand/RVOL evidence).
 */
export function strategyRdWarmCandlesForRequest(
  candles: readonly StrategyRdOhlcBar[] | null | undefined
): StrategyRdOhlcBar[] | undefined {
  if (!candles?.length) return undefined;
  if (!strategyRdCandlesHaveVolume(candles)) return undefined;
  return [...candles];
}

function asCount(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return value;
}

/** Debug / caption: setup hits → after confluence filter. */
export function strategyRdDetectFilterCountLabel(
  stats: StrategyRdOhlcStats | null | undefined
): string | null {
  if (!stats) return null;
  const setup = asCount(stats.setup_match_count);
  const after =
    asCount(stats.after_filter_count) ?? asCount(stats.match_count);
  if (setup != null && after != null && setup !== after) {
    return `${setup} → ${after}`;
  }
  if (after != null) return `${after} match${after === 1 ? "" : "es"}`;
  if (setup != null) return `${setup} setup match${setup === 1 ? "" : "es"}`;
  return null;
}

export function strategyRdDetectWindowKey(
  candles: readonly StrategyRdOhlcBar[] | null | undefined
): string {
  if (!candles?.length) return "";
  const first = candles[0]?.timestamp ?? "";
  const last = candles[candles.length - 1]?.timestamp ?? "";
  return `${first}|${last}|${candles.length}`;
}

/**
 * Full-chart overlays: wash every hit; label Ready / Break / Enter only for the
 * selected match so multi-hit windows stay readable.
 */
export function strategyRdDetectChartHighlights(opts: {
  mode: StrategyRdDetectMode;
  matches: readonly StrategyRdMatch[];
  markers: readonly StrategyRdMatchMarker[];
  selectedMatch: StrategyRdMatch | null;
}): StrategyRdDetectHighlight[] {
  const out: StrategyRdDetectHighlight[] = [];
  const seen = new Set<string>();

  const push = (row: StrategyRdDetectHighlight, kind: string) => {
    const time = row.barTime.trim();
    if (!time) return;
    const key = `${time}|${kind}|${row.label}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ ...row, barTime: time });
  };

  const pushPath = (row: StrategyRdMatch) => {
    const dir = strategyRdMatchDisplayDirection(row);
    const entryMode = row.path?.entry_mode || "";
    const locationConcept = row.path?.location_fill || null;
    const markers = strategyRdPathChartMarkers(row.path, {
      includeSetup: false,
      setupLabel: strategyRdConceptChartLabel(row.setup_id || row.pattern_id),
    });
    for (const marker of markers) {
      const kind = marker.kind;
      // Prefer label already resolved on the marker (includes event concept_id + role).
      const label =
        kind === "setup"
          ? marker.label
          : marker.label ||
            strategyRdPathEventChartLabel(kind, entryMode, {
              conceptId: locationConcept,
            });
      push(
        {
          barTime: marker.barTime,
          direction: marker.direction ?? dir,
          label,
          showText: true,
          showArrow: true,
          // Selected story: labels + geometry only — no dark wash over text.
          showWash: false,
          emphasis: "normal",
          // Rule-entry order / cancel read as plain labelled markers.
          kind: kind === "order_placed" || kind === "cancel" ? "marker" : kind,
        },
        kind
      );
    }

    if (row.path?.status === "incomplete") {
      const invalidLabel = strategyRdPathIncompleteChartLabel(row.path.invalid_reason);
      if (invalidLabel) {
        const anchor =
          markers.find((m) => m.kind === "rejection")?.barTime ||
          markers.find((m) => m.kind === "break")?.barTime ||
          markers.find((m) => m.kind === "setup")?.barTime ||
          row.path.setup_bar_time?.trim() ||
          row.bar_time;
        push(
          {
            barTime: anchor,
            direction: dir,
            label: invalidLabel,
            showText: true,
            showArrow: true,
            showWash: false,
            emphasis: "normal",
            kind: "invalid",
          },
          "invalid"
        );
      }
    }
  };

  const pushWash = (row: StrategyRdMatch) => {
    const dir = strategyRdMatchDisplayDirection(row);
    const setup =
      row.path?.setup_bar_time?.trim() ||
      row.bar_time?.trim() ||
      "";
    // Entry wash/arrow only when path is ready (same gate as Enter labels).
    const entry =
      row.path?.status === "ready" ? row.path?.entry_bar_time?.trim() || "" : "";
    // Same bar → treat as fill (clearer wash + arrow).
    if (setup && (!entry || entry === setup)) {
      const isFill = Boolean(entry && entry === setup);
      push(
        {
          barTime: setup,
          direction: dir,
          label: "",
          showText: false,
          showArrow: isFill,
          showWash: true,
          emphasis: isFill ? "normal" : "muted",
          kind: isFill ? "entry" : "marker",
        },
        `wash:${setup}`
      );
      return;
    }
    if (setup) {
      push(
        {
          barTime: setup,
          direction: dir,
          label: "",
          showText: false,
          showArrow: false,
          showWash: true,
          emphasis: "muted",
          kind: "marker",
        },
        `wash-setup:${setup}`
      );
    }
    if (entry) {
      push(
        {
          barTime: entry,
          direction: dir,
          label: "",
          showText: false,
          showArrow: true,
          showWash: true,
          emphasis: "normal",
          kind: "entry",
        },
        `wash-entry:${entry}`
      );
    }
  };

  const selectedKey = opts.selectedMatch ? strategyRdMatchKey(opts.selectedMatch) : null;
  const pathRows = opts.matches.length
    ? opts.matches
    : opts.selectedMatch
      ? [opts.selectedMatch]
      : [];
  for (let i = 0; i < pathRows.length; i += 1) {
    const row = pathRows[i]!;
    const isFocus = selectedKey
      ? strategyRdMatchKey(row) === selectedKey
      : i === 0;
    if (isFocus) pushPath(row);
    else pushWash(row);
  }

  for (const marker of opts.markers) {
    const time = marker.bar_time.trim();
    if (!time) continue;
    if (out.some((row) => row.barTime === time && row.kind !== "marker")) continue;
    // Selected path already labeled; other marker rows stay wash-only.
    const isSelectedSetup =
      opts.selectedMatch &&
      (opts.selectedMatch.bar_time === time ||
        opts.selectedMatch.path?.setup_bar_time === time);
    const patternId = (marker.pattern_id || marker.setup_id || "").toLowerCase();
    const isLiqPattern =
      patternId.includes("liq") &&
      (patternId.includes("sweep") || patternId.includes("run"));
    // Liquidity Take/Sweep|Run pills come from meta.markers via concept drawings —
    // never a duplicate "Run"/"Liq" triangle on the signal bar.
    const showConceptTag = Boolean(isSelectedSetup) && !isLiqPattern;
    push(
      {
        barTime: time,
        direction: marker.direction,
        label: showConceptTag
          ? strategyRdConceptChartLabel(marker.pattern_id || marker.setup_id)
          : "",
        showText: showConceptTag,
        showArrow: Boolean(isSelectedSetup),
        showWash: !isSelectedSetup,
        emphasis: isSelectedSetup ? "normal" : "muted",
        kind: "marker",
      },
      "marker"
    );
  }

  return out;
}

/** Click target times for selecting a match from the chart (setup + path events). */
export function strategyRdMatchChartSelectHits(
  matches: readonly StrategyRdMatch[]
): Array<{ id: string; timeMs: number }> {
  const out: Array<{ id: string; timeMs: number }> = [];
  for (const match of matches) {
    const id = strategyRdMatchKey(match);
    const times = new Set<string>();
    const setup = match.path?.setup_bar_time?.trim() || match.bar_time?.trim();
    if (setup) times.add(setup);
    const entry = match.path?.entry_bar_time?.trim();
    if (entry) times.add(entry);
    for (const ev of match.path?.events ?? []) {
      const t = ev.bar_time?.trim();
      if (t) times.add(t);
    }
    for (const t of Array.from(times)) {
      const timeMs = executionCandleTimeToMillis(t);
      if (timeMs == null || !Number.isFinite(timeMs)) continue;
      out.push({ id, timeMs });
    }
  }
  return out;
}

/** Nearest match key to a chart crosshair time (within one bar on typical 5m). */
export function pickStrategyRdMatchKeyFromChartTime(
  hits: readonly { id: string; timeMs: number }[],
  timeMs: number,
  toleranceMs = 15 * 60 * 1000
): string | null {
  if (!hits.length || !Number.isFinite(timeMs)) return null;
  let best: { id: string; dist: number } | null = null;
  for (const hit of hits) {
    const dist = Math.abs(hit.timeMs - timeMs);
    if (dist > toleranceMs) continue;
    if (!best || dist < best.dist) best = { id: hit.id, dist };
  }
  return best?.id ?? null;
}
